import { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { X, Scan, Telescope, Radio, Send, MessageCircle, Mail, Users, ArrowLeftRight, Radar, Rocket, Satellite, RadioTower, Clock, Coins, Lock } from 'lucide-react';
import { useStarMap, useScanStar, useSendMessage } from '../../hooks/useStars';
import { useInbox, useOutbox, useReplyMessage } from '../../hooks/useMessages';
import { useBroadcastSignal, useCreateSpaceMission, useRelationAction, useRunSpaceAction, useSpaceOverview } from '../../hooks/useSpace';
import type { StarData, ConstellationData } from '../../api/stars';
import type { StarMessageItem } from '../../api/messages';
import type { MissionType, RelationActionType, SpaceOption, TechnologyActionType } from '../../api/space';

interface StarMapModalProps {
  stage: number;
  onClose: () => void;
}

const MESSAGE_TEMPLATES = [
  { key: 'greeting', label: 'Greetings', icon: 'Hi', preview: 'Greetings from our world!' },
  { key: 'trade', label: 'Trade Offer', icon: 'Tr', preview: 'We seek trade partners.' },
  { key: 'info_request', label: 'Information Request', icon: '?', preview: 'Tell us about your civilization.' },
  { key: 'alliance', label: 'Alliance Proposal', icon: 'Al', preview: 'Would you consider an alliance?' },
  { key: 'help', label: 'Request Help', icon: '!', preview: 'Our world faces difficulties.' },
  { key: 'share_map', label: 'Share Star Map', icon: 'Map', preview: 'Sharing our star chart discoveries.' },
];

function visibleOptions<T extends { available: boolean }>(options: T[] = []) {
  const available = options.filter((option) => option.available);
  const firstLocked = options.find((option) => !option.available);
  return firstLocked ? [...available, firstLocked] : available;
}

function OptionMeta({ option }: { option: Pick<SpaceOption, 'cost' | 'durationDays' | 'travelDays' | 'lockedReason'> }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
      <span className="flex items-center gap-1"><Coins className="h-3 w-3" />{option.cost}cr</span>
      {option.durationDays && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{option.durationDays}d</span>}
      {option.travelDays && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />arrives {option.travelDays}d</span>}
      {option.lockedReason && <span className="flex items-center gap-1 text-amber-400"><Lock className="h-3 w-3" />{option.lockedReason}</span>}
    </div>
  );
}

export default function StarMapModal({ stage, onClose }: StarMapModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useStarMap();
  const scanStar = useScanStar();
  const sendMessage = useSendMessage();
  const inboxQuery = useInbox();
  const outboxQuery = useOutbox();
  const spaceOverview = useSpaceOverview();
  const runSpaceAction = useRunSpaceAction();
  const broadcastSignal = useBroadcastSignal();
  const createMission = useCreateSpaceMission();
  const relationAction = useRelationAction();
  /* const markRead = useMarkRead(); */
  const replyMessage = useReplyMessage();

  const canScan = data?.canScan ?? false;
  const hasStarStudy = data?.hasStarStudy ?? false;
  const scanRequirement = data?.scanRequirement || (stage < 6 ? 'Scanning requires Technology stage' : 'Build the required observatory upgrades');
  const radioActive = data?.radioActive ?? false;
  const unreadCount = inboxQuery.data?.unreadCount ?? 0;

  const [hoveredStar, setHoveredStar] = useState<StarData | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedStar, setSelectedStar] = useState<number | null>(null);
  const [showComposer, setShowComposer] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [messageTab, setMessageTab] = useState<'inbox' | 'outbox'>('inbox');
  const [selectedTemplate, setSelectedTemplate] = useState('greeting');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [missionType, setMissionType] = useState<MissionType>('probe');

  const selectedDiscovery = selectedStar !== null ? data?.discoveries[selectedStar] : undefined;
  /* const selectedStarData = selectedStar !== null ? data?.stars.find((s) => s.index === selectedStar) : undefined; */
  const occupiedStar = data?.occupiedStars.find((o) => o.starIndex === selectedStar);
  const targetOptions = selectedStar !== null ? spaceOverview.data?.targetOptions?.[selectedStar] : undefined;
  const selectedMissionOption = targetOptions?.missionOptions.find((item) => item.id === missionType);
  const directionalSignal = targetOptions?.signalOptions[0];
  const visibleSpaceActions = visibleOptions(spaceOverview.data?.actions || []);

  useEffect(() => {
    if (!containerRef.current || !data || isLoading) return;

    // Remove any stale canvas from previous mounts or failed renders
    containerRef.current.querySelectorAll('canvas').forEach((c) => c.remove());

    let app: PIXI.Application | null = null;
    let rafId = 0;

    const init = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Wait for the container to have actual dimensions before creating the canvas.
      // This is critical when the modal first opens — the flex layout may not be
      // computed yet, resulting in a 0×0 canvas and invisible stars.
      if (rect.width === 0 || rect.height === 0) {
        rafId = requestAnimationFrame(init);
        return;
      }

      app = new PIXI.Application({
        backgroundAlpha: 0,
        resizeTo: containerRef.current,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      const canvas = app.view as HTMLCanvasElement;
      canvas.style.maxWidth = '100%';
      canvas.style.maxHeight = '100%';
      containerRef.current.appendChild(canvas);

      const starContainer = new PIXI.Container();
      app.stage.addChild(starContainer);

      const lineContainer = new PIXI.Container();
      app.stage.addChild(lineContainer);

      const scaleX = app.screen.width / 1000;
      const scaleY = app.screen.height / 600;
      const scale = Math.min(scaleX, scaleY);
      const offsetX = (app.screen.width - 1000 * scale) / 2;
      const offsetY = (app.screen.height - 600 * scale) / 2;

      // Draw constellation lines
      data.constellations.forEach((c: ConstellationData) => {
        const line = new PIXI.Graphics();
        line.lineStyle(1, 0xFFFFFF, 0.15);
        for (let i = 0; i < c.starIndices.length - 1; i++) {
          const s1 = data.stars[c.starIndices[i]];
          const s2 = data.stars[c.starIndices[i + 1]];
          line.moveTo(s1.x * scale + offsetX, s1.y * scale + offsetY);
          line.lineTo(s2.x * scale + offsetX, s2.y * scale + offsetY);
        }
        lineContainer.addChild(line);
      });

      const starSprites: { sprite: PIXI.Graphics; baseAlpha: number; twinkleSpeed: number; twinklePhase: number }[] = [];

      data.stars.forEach((star: StarData) => {
        const graphics = new PIXI.Graphics();
        graphics.x = star.x * scale + offsetX;
        graphics.y = star.y * scale + offsetY;

        // Core body
        graphics.beginFill(parseInt(star.color.replace('#', ''), 16), star.brightness);
        graphics.drawCircle(0, 0, star.size * scale * 0.8);
        graphics.endFill();

        // Glow for larger stars
        if (star.size > 2) {
          graphics.beginFill(parseInt(star.color.replace('#', ''), 16), 0.2);
          graphics.drawCircle(0, 0, star.size * scale * 2);
          graphics.endFill();
        }

        const discovery = data.discoveries[star.index];
        const occupied = data.occupiedStars.find((o) => o.starIndex === star.index);
        const commStatus = discovery?.communicationStatus;

        // Discovery ring (cyan)
        if (discovery) {
          graphics.lineStyle(1.5, 0x00FFFF, 0.6);
          graphics.drawCircle(0, 0, star.size * scale * 2.5);
        }

        // Occupied star ring (green)
        if (occupied && !discovery) {
          graphics.lineStyle(1.5, 0x00FF00, 0.5);
          graphics.drawCircle(0, 0, star.size * scale * 2.5);
        }

        // Two-way communication ring (gold)
        if (commStatus === 'two_way') {
          graphics.lineStyle(2, 0xFFD700, 0.8);
          graphics.drawCircle(0, 0, star.size * scale * 3.2);
        }

        // Home star indicator (white diamond)
        if (data.homeStarIndex === star.index) {
          graphics.lineStyle(1.5, 0xFFFFFF, 0.9);
          graphics.drawRect(-star.size * scale * 1.5, -star.size * scale * 1.5, star.size * scale * 3, star.size * scale * 3);
        }

        graphics.eventMode = 'static';
        graphics.cursor = 'pointer';

        graphics.on('pointerover', () => {
          setHoveredStar(star);
          const bounds = graphics.getBounds();
          setTooltipPos({ x: bounds.x + bounds.width / 2, y: bounds.y });
        });
        graphics.on('pointerout', () => {
          setHoveredStar(null);
        });
        graphics.on('pointerdown', () => {
          setSelectedStar(star.index);
          setShowComposer(false);
          setReplyingTo(null);
        });

        starContainer.addChild(graphics);
        starSprites.push({
          sprite: graphics,
          baseAlpha: star.brightness,
          twinkleSpeed: star.twinkleSpeed,
          twinklePhase: star.twinklePhase,
        });
      });

      // Twinkle animation
      let tick = 0;
      app.ticker.add(() => {
        tick += 0.016;
        starSprites.forEach((s) => {
          const twinkle = Math.sin(tick * s.twinkleSpeed * 60 + s.twinklePhase) * 0.3;
          s.sprite.alpha = Math.max(0.2, Math.min(1, s.baseAlpha + twinkle));
        });
      });
    };

    rafId = requestAnimationFrame(init);

    return () => {
      cancelAnimationFrame(rafId);
      app?.destroy(true, { children: true });
    };
  }, [data, isLoading]);

  const handleSend = () => {
    if (selectedStar === null) return;
    sendMessage.mutate({ index: selectedStar, templateKey: selectedTemplate }, {
      onSuccess: () => {
        setShowComposer(false);
      },
    });
  };

  const handleReply = (messageId: string) => {
    replyMessage.mutate({ messageId, templateKey: selectedTemplate }, {
      onSuccess: () => {
        setReplyingTo(null);
      },
    });
  };

  const runAction = (actionType: TechnologyActionType) => {
    runSpaceAction.mutate({ actionType });
  };

  const sendSignal = () => {
    if (selectedStar !== null && !directionalSignal?.available) return;
    broadcastSignal.mutate({
      signalType: selectedStar === null ? 'omni_question' : 'directional_question',
      directionStarIndex: selectedStar ?? undefined,
    });
  };

  const launchMission = () => {
    if (selectedStar === null && !selectedDiscovery?.linkedPlanetId) return;
    if (!selectedMissionOption?.available) return;
    createMission.mutate({
      missionType,
      targetPlanetId: selectedDiscovery?.linkedPlanetId || undefined,
      targetStarIndex: selectedStar ?? undefined,
    });
  };

  const runRelationAction = (action: RelationActionType) => {
    if (!selectedDiscovery?.linkedPlanetId) return;
    const option = targetOptions?.relationActions.find((item) => item.id === action);
    if (!option?.available) return;
    relationAction.mutate({ planetId: selectedDiscovery.linkedPlanetId, action });
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case 'transit': return 'In transit';
      case 'received': return 'Received';
      case 'read': return 'Read';
      case 'replied': return 'Replied';
      default: return status;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full h-full max-w-none bg-slate-900/95 border-0 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Telescope className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">Star Map</h2>
            {radioActive && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-900/50 border border-green-700/50 rounded-full text-[10px] text-green-400">
                <Radio className="w-3 h-3" /> Radio Online
              </span>
            )}
            {!canScan && (
              <span className="px-2 py-0.5 bg-amber-900/50 border border-amber-700/50 rounded-full text-[10px] text-amber-400">
                {scanRequirement}
              </span>
            )}
            {hasStarStudy && data?.homeStarProfile && (
              <span className="hidden sm:flex items-center gap-1 px-2 py-0.5 bg-slate-800/70 border border-slate-700 rounded-full text-[10px] text-slate-300">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: data.homeStarProfile.colorHex }}
                />
                {data.homeStarProfile.spectralClass}-class, {data.homeStarProfile.temperatureK}K
              </span>
            )}
            {data && (
              <span className="hidden md:flex items-center gap-1 px-2 py-0.5 bg-cyan-950/50 border border-cyan-700/40 rounded-full text-[10px] text-cyan-300">
                <Radar className="w-3 h-3" />
                Tier {data.scannerTier} · {Math.round(data.detectionConfidence)}% confidence
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowMessages(!showMessages); setSelectedStar(null); }}
              className="relative flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 bg-red-500 rounded-full text-[9px] text-white font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Star canvas */}
          <div className="flex-1 relative" ref={containerRef}>
            {/* Tooltip */}
            {hoveredStar && (
              <div
                className="absolute z-20 px-3 py-2 bg-slate-800/90 backdrop-blur-sm border border-slate-700 rounded-lg text-xs pointer-events-none"
                style={{ left: tooltipPos.x + 10, top: tooltipPos.y - 10 }}
              >
                <div className="font-semibold text-white">Star #{hoveredStar.index}</div>
                {data?.discoveries[hoveredStar.index] ? (
                  <div className="text-cyan-400 mt-0.5">
                    {data.discoveries[hoveredStar.index].starType} — {data.discoveries[hoveredStar.index].distance.toFixed(1)} ly
                    {data.discoveries[hoveredStar.index].hasPlanet && (
                      <span className="text-green-400 ml-1">🪐 Planet detected</span>
                    )}
                  </div>
                ) : data?.occupiedStars.find((o) => o.starIndex === hoveredStar.index) ? (
                  <div className="text-green-400 mt-0.5">🪐 Unscanned planet</div>
                ) : (
                  <div className="text-slate-400 mt-0.5">{canScan ? 'Click to scan (50cr)' : scanRequirement}</div>
                )}
              </div>
            )}
          </div>

          {/* Early-game overlay when no side panel */}
          {!(stage >= 6 || canScan || (data?.discoveries && Object.keys(data.discoveries).length > 0)) && (
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-lg text-xs text-slate-400 pointer-events-none">
              Scan stars to discover what lies beyond your system
            </div>
          )}

          {/* Side panel — only in later game */}
          {(selectedStar !== null || showMessages || (stage >= 6 || canScan || (data?.discoveries && Object.keys(data.discoveries).length > 0))) && (
            <div className="w-80 max-w-[45vw] border-l border-slate-700 bg-slate-900/90 flex flex-col">
              {selectedStar === null && !showMessages && (
                <div className="p-4 overflow-y-auto space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Satellite className="w-4 h-4 text-cyan-400" />
                      <h3 className="text-sm font-semibold text-white">Space Program</h3>
                    </div>
                    {spaceOverview.data ? (
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
                          <div className="text-slate-500">Scan</div>
                          <div className="font-mono text-cyan-300">{Math.round(spaceOverview.data.capabilities.scanCoverage)}%</div>
                        </div>
                        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
                          <div className="text-slate-500">Reach</div>
                          <div className="font-mono text-cyan-300">{Math.round(spaceOverview.data.capabilities.reachableRangeLy)} ly</div>
                        </div>
                        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
                          <div className="text-slate-500">Goal</div>
                          <div className="truncate text-amber-300">{spaceOverview.data.factionGoal?.label || 'Unformed'}</div>
                        </div>
                        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
                          <div className="text-slate-500">Score</div>
                          <div className="font-mono text-amber-300">{spaceOverview.data.score?.score || 0}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500">Loading program data...</div>
                    )}
                    {spaceOverview.data?.capabilities.spaceMetrics && (
                      <div className="mt-3 space-y-1">
                        <h4 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Program Metrics</h4>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
                          <div className="flex justify-between"><span className="text-slate-500">Signal Clarity</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.signalClarity}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Signal Reach</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.signalReach}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Scanner Calib.</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.scannerCalibration}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">System Analysis</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.systemAnalysis}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Launch Ready</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.launchReadiness}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Envoy Ready</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.envoyReadiness}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Defence Ready</span><span className="text-cyan-300">{spaceOverview.data.capabilities.spaceMetrics.defenceReadiness}</span></div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <RadioTower className="w-4 h-4 text-indigo-400" />
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Space Operations</h4>
                    </div>
                    {spaceOverview.data?.operationState.active && (
                      <div className="mb-2 rounded-lg border border-indigo-700/50 bg-indigo-950/25 p-2 text-xs text-indigo-100">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{spaceOverview.data.operationState.active.label}</span>
                          <span className="text-indigo-300">{spaceOverview.data.operationState.active.remainingDays}d left</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-800">
                          <div
                            className="h-full rounded-full bg-indigo-400"
                            style={{
                              width: `${Math.min(100, Math.max(5, ((spaceOverview.data.operationState.active.durationDays - spaceOverview.data.operationState.active.remainingDays) / spaceOverview.data.operationState.active.durationDays) * 100))}%`,
                            }}
                          />
                        </div>
                        <div className="mt-1 text-[10px] text-slate-400">{spaceOverview.data.operationState.active.effectPreview}</div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {visibleSpaceActions.map((action) => (
                        <button
                          key={action.id}
                          disabled={!action.available || runSpaceAction.isPending}
                          onClick={() => runAction(action.id)}
                          className="w-full rounded-lg border border-slate-700 bg-slate-800/70 px-2.5 py-2 text-left text-[10px] text-slate-300 hover:border-indigo-500/60 disabled:opacity-45"
                          title={`Requires ${action.requirement}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-slate-200">{action.label}</span>
                            <span className="text-slate-500">{action.phase}</span>
                          </div>
                          <div className="mt-1 text-slate-400">{action.description}</div>
                          <OptionMeta option={action} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Radio className="w-4 h-4 text-green-400" />
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Signals</h4>
                    </div>
                    <button
                      onClick={sendSignal}
                      disabled={!spaceOverview.data?.capabilities.radio || broadcastSignal.isPending}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-green-700 px-3 py-2 text-xs font-medium text-white hover:bg-green-600 disabled:bg-slate-700 disabled:text-slate-500"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Omni Broadcast - 50cr / 2d
                    </button>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-4 h-4 text-orange-400" />
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Mission Traffic</h4>
                    </div>
                    <div className="space-y-1.5">
                      {spaceOverview.data?.missions.all.slice(0, 5).map((mission) => (
                        <div key={mission.mission_id} className="rounded-lg border border-slate-700 bg-slate-800/60 px-2.5 py-2 text-[11px] text-slate-300">
                          <div className="flex justify-between gap-2">
                            <span className="capitalize">{mission.mission_type.replace('_', ' ')}</span>
                            <span className="text-slate-500">{mission.status}</span>
                          </div>
                          <div className="text-slate-500">Arrives day {mission.arrival_day}</div>
                        </div>
                      ))}
                      {spaceOverview.data?.missions.all.length === 0 && (
                        <div className="rounded-lg border border-dashed border-slate-700 px-3 py-4 text-center text-xs text-slate-500">
                          Select a scanned star to review signals, probes, missions, and late-game social options.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Star detail panel */}
              {selectedStar !== null && !showMessages && (
                <>
                  <div className="p-4 border-b border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-white">Star #{selectedStar}</h3>
                      <button onClick={() => setSelectedStar(null)} className="p-1 hover:bg-slate-800 rounded">
                        <X className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>

                    {selectedDiscovery ? (
                      <div className="space-y-2">
                        <div className="text-cyan-400 font-medium">{selectedDiscovery.starType}</div>
                        <div className="text-xs text-slate-400">{selectedDiscovery.distance.toFixed(1)} light years</div>
                        <div className="text-xs text-slate-400">Habitability: {selectedDiscovery.habitability}%</div>

                        {selectedDiscovery.hasPlanet && (
                          <div className="mt-3 p-2.5 bg-slate-800/70 rounded-lg border border-slate-700">
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-3.5 h-3.5 text-green-400" />
                              <span className="text-sm font-medium text-green-400">Civilization Detected</span>
                            </div>
                            <div className="text-xs text-slate-300">{selectedDiscovery.linkedPlanetName}</div>
                            <div className="text-xs text-slate-500">Stage {selectedDiscovery.linkedPlanetStage}</div>
                            {selectedDiscovery.communicationStatus === 'two_way' && (
                              <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-400">
                                <ArrowLeftRight className="w-3 h-3" /> Two-way communication established
                              </div>
                            )}
                          </div>
                        )}

                        {/* Message composer */}
                        {selectedDiscovery.hasPlanet && (
                          <>
                            {!showComposer ? (
                              <button
                                onClick={() => setShowComposer(true)}
                                disabled={!radioActive}
                                className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-xs font-medium text-white transition-colors"
                              >
                                <Send className="w-3.5 h-3.5" />
                                {radioActive ? 'Send Message' : 'Radio Tower Required'}
                              </button>
                            ) : (
                              <div className="mt-2 space-y-2">
                                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Message Template</div>
                                <div className="space-y-1 max-h-36 overflow-y-auto">
                                  {MESSAGE_TEMPLATES.map((t) => (
                                    <button
                                      key={t.key}
                                      onClick={() => setSelectedTemplate(t.key)}
                                      className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                                        selectedTemplate === t.key
                                          ? 'bg-indigo-900/50 border border-indigo-500/50 text-indigo-300'
                                          : 'bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:bg-slate-800'
                                      }`}
                                    >
                                      <span className="mr-1">{t.icon}</span>
                                      {t.label}
                                    </button>
                                  ))}
                                </div>
                                <div className="text-[10px] text-slate-500">
                                  Cost: {Math.max(10, Math.ceil((selectedDiscovery.distance || 1) * 10))} credits
                                  {' · '}
                                  Travel: {Math.max(1, Math.ceil((selectedDiscovery.distance || 1) / 10))} day(s)
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleSend}
                                    disabled={sendMessage.isPending}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 rounded-lg text-xs font-medium text-white transition-colors"
                                  >
                                    <Send className="w-3.5 h-3.5" />
                                    {sendMessage.isPending ? 'Sending...' : 'Launch'}
                                  </button>
                                  <button
                                    onClick={() => setShowComposer(false)}
                                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        <div className="mt-3 space-y-2 rounded-lg border border-slate-700 bg-slate-800/50 p-2.5">
                          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            <Rocket className="w-3.5 h-3.5 text-orange-400" />
                            Space Actions
                          </div>
                          <button
                            onClick={sendSignal}
                            disabled={broadcastSignal.isPending || !radioActive || !directionalSignal?.available}
                            className="flex w-full items-center justify-center gap-1 rounded-lg bg-green-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-green-600 disabled:bg-slate-700"
                          >
                            <Radio className="w-3.5 h-3.5" />
                            Directional Signal
                          </button>
                          {directionalSignal && <OptionMeta option={directionalSignal} />}

                          <div className="space-y-1.5">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Missions</div>
                            {visibleOptions(targetOptions?.missionOptions || []).map((item) => (
                              <button
                                key={item.id}
                                onClick={() => setMissionType(item.id as MissionType)}
                                disabled={!item.available}
                                className={`w-full rounded-lg border px-2 py-1.5 text-left text-[10px] ${
                                  missionType === item.id
                                    ? 'border-orange-500/60 bg-orange-950/25 text-orange-100'
                                    : 'border-slate-700 bg-slate-900 text-slate-300'
                                } disabled:opacity-45`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-semibold">{item.label}</span>
                                  {item.risk && <span className="text-amber-400">{item.risk}</span>}
                                </div>
                                <div className="mt-0.5 text-slate-500">{item.description}</div>
                                <OptionMeta option={item} />
                              </button>
                            ))}
                            <button
                              onClick={launchMission}
                              disabled={createMission.isPending || !selectedMissionOption?.available}
                              className="w-full rounded-lg bg-orange-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-orange-500 disabled:bg-slate-700"
                            >
                              Launch Selected Mission
                            </button>
                          </div>

                          {selectedDiscovery.linkedPlanetId && (
                            <div className="space-y-1.5">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Relations</div>
                              {visibleOptions(targetOptions?.relationActions || []).map((item) => (
                                <button
                                  key={item.id}
                                  onClick={() => runRelationAction(item.id as RelationActionType)}
                                  disabled={!item.available || relationAction.isPending}
                                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-left text-[10px] text-slate-300 hover:border-amber-500/60 disabled:opacity-45"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold">{item.label}</span>
                                    {item.risk && <span className="text-amber-400">{item.risk}</span>}
                                  </div>
                                  <div className="mt-0.5 text-slate-500">{item.description}</div>
                                  <OptionMeta option={item} />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : occupiedStar ? (
                      <div className="space-y-2">
                        <div className="text-green-400 font-medium">Planet Detected</div>
                        <div className="text-xs text-slate-400">{occupiedStar.planetName}</div>
                        <div className="text-xs text-slate-500">Stage {occupiedStar.planetStage}</div>
                        <div className="text-xs text-amber-400 mt-1">Scan to establish contact</div>
                        {canScan && (
                          <button
                            onClick={() => scanStar.mutate(selectedStar)}
                            disabled={scanStar.isPending}
                            className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 rounded-lg text-xs font-medium text-white transition-colors"
                          >
                            <Scan className="w-3.5 h-3.5" />
                            {scanStar.isPending ? 'Scanning...' : 'Scan Star (50cr)'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-slate-400 text-sm">Unexplored</div>
                        {canScan && (
                          <button
                            onClick={() => scanStar.mutate(selectedStar)}
                            disabled={scanStar.isPending}
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 rounded-lg text-xs font-medium text-white transition-colors"
                          >
                            <Scan className="w-3.5 h-3.5" />
                            {scanStar.isPending ? 'Scanning...' : 'Scan Star (50cr)'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Messages panel */}
              {showMessages && (
                <>
                  <div className="flex items-center gap-1 p-2 border-b border-slate-700">
                    <button
                      onClick={() => setMessageTab('inbox')}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        messageTab === 'inbox' ? 'bg-indigo-900/50 text-indigo-300' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Inbox {inboxQuery.data?.unreadCount ? `(${inboxQuery.data.unreadCount})` : ''}
                    </button>
                    <button
                      onClick={() => setMessageTab('outbox')}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        messageTab === 'outbox' ? 'bg-indigo-900/50 text-indigo-300' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Outbox
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {messageTab === 'inbox' ? (
                      inboxQuery.isLoading ? (
                        <div className="text-xs text-slate-500 text-center py-4">Loading...</div>
                      ) : inboxQuery.data?.messages.length === 0 ? (
                        <div className="text-xs text-slate-500 text-center py-4">No messages yet</div>
                      ) : (
                        inboxQuery.data?.messages.map((msg: StarMessageItem) => (
                          <div
                            key={msg.messageId}
                            className={`p-2.5 rounded-lg border text-xs ${
                              msg.status === 'received'
                                ? 'bg-indigo-900/20 border-indigo-500/30'
                                : 'bg-slate-800/50 border-slate-700/50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-slate-200">{msg.sender.planetName}</span>
                              <span className={`text-[10px] ${msg.status === 'received' ? 'text-indigo-400' : 'text-slate-500'}`}>
                                {formatStatus(msg.status)}
                              </span>
                            </div>
                            <div className="text-slate-400 mb-1">
                              {MESSAGE_TEMPLATES.find((t) => t.key === msg.templateKey)?.preview || msg.templateKey}
                            </div>
                            {msg.customText && <div className="text-slate-500 italic mb-1">&ldquo;{msg.customText}&rdquo;</div>}
                            <div className="text-[10px] text-slate-600">
                              {msg.distanceLy.toFixed(1)} ly · Day {msg.arrivalDay}
                            </div>
                            {msg.status === 'received' && radioActive && (
                              <button
                                onClick={() => setReplyingTo(msg.messageId)}
                                className="mt-1.5 flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300"
                              >
                                <MessageCircle className="w-3 h-3" /> Reply
                              </button>
                            )}
                            {replyingTo === msg.messageId && (
                              <div className="mt-2 space-y-1.5">
                                <div className="flex gap-1 flex-wrap">
                                  {MESSAGE_TEMPLATES.map((t) => (
                                    <button
                                      key={t.key}
                                      onClick={() => setSelectedTemplate(t.key)}
                                      className={`px-2 py-0.5 rounded text-[10px] ${
                                        selectedTemplate === t.key ? 'bg-indigo-900/50 text-indigo-300' : 'bg-slate-800 text-slate-400'
                                      }`}
                                    >
                                      {t.icon} {t.label}
                                    </button>
                                  ))}
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleReply(msg.messageId)}
                                    disabled={replyMessage.isPending}
                                    className="flex-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-[10px] text-white"
                                  >
                                    {replyMessage.isPending ? 'Sending...' : 'Send Reply'}
                                  </button>
                                  <button
                                    onClick={() => setReplyingTo(null)}
                                    className="px-2 py-1 bg-slate-800 rounded text-[10px] text-slate-400"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                            {msg.status === 'received' && !radioActive && (
                              <div className="mt-1 text-[10px] text-amber-500">Radio Tower required to reply</div>
                            )}
                          </div>
                        ))
                      )
                    ) : outboxQuery.isLoading ? (
                      <div className="text-xs text-slate-500 text-center py-4">Loading...</div>
                    ) : outboxQuery.data?.messages.length === 0 ? (
                      <div className="text-xs text-slate-500 text-center py-4">No sent messages</div>
                    ) : (
                      outboxQuery.data?.messages.map((msg: StarMessageItem) => (
                        <div key={msg.messageId} className="p-2.5 rounded-lg border bg-slate-800/50 border-slate-700/50 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-slate-200">To: {msg.receiver?.planetName || 'Unknown'}</span>
                            <span className="text-[10px] text-slate-500">{formatStatus(msg.status)}</span>
                          </div>
                          <div className="text-slate-400 mb-1">
                            {MESSAGE_TEMPLATES.find((t) => t.key === msg.templateKey)?.preview || msg.templateKey}
                          </div>
                          {msg.customText && <div className="text-slate-500 italic mb-1">&ldquo;{msg.customText}&rdquo;</div>}
                          <div className="text-[10px] text-slate-600">
                            {msg.distanceLy.toFixed(1)} ly · Arrives day {msg.arrivalDay}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-700 bg-slate-900">
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-cyan-400/60" /> Scanned
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-400/60" /> Has Planet
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-400/60" /> Two-way
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-white/80" /> Home
            </span>
          </div>
          {(stage >= 6 || canScan) && (
            <div className="flex items-center gap-1.5 text-xs text-cyan-400">
              <Scan className="w-3.5 h-3.5" />
              <span>{Object.keys(data?.discoveries || {}).length} scanned</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
